"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Swipe = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const NativeListener_1 = require("../NativeListener");
const MINIMUM_SWIPE_OFFSET = 15;
const MIN_INERTIA = 0.3;
function Swipe({ horizontal = true, vertical = false, swipeBoundary, onSwipeStart, onSwipeMove, onSwipeEnd, children, }) {
    const state = (0, react_1.useRef)();
    const touchstart = (0, react_1.useCallback)((ev) => {
        state.current = handleTouchStart(ev, { horizontal, vertical });
    }, [horizontal, vertical]);
    const touchmove = (0, react_1.useCallback)((ev) => {
        var _a;
        const wasSwiping = !!((_a = state.current) === null || _a === void 0 ? void 0 : _a.axis);
        if (!ev.cancelable && !wasSwiping) {
            state.current = undefined;
        }
        else if (state.current) {
            const newState = handleTouchMove(ev, state.current);
            const isSwiping = !!newState.axis;
            if (isSwiping) {
                ev.preventDefault();
                ev.stopPropagation();
            }
            state.current = newState;
            const data = swipeData(state.current, getBoundary(ev, state.current.axis, swipeBoundary));
            if (isSwiping) {
                if (!wasSwiping && onSwipeStart) {
                    onSwipeStart(data);
                }
                if (onSwipeMove) {
                    onSwipeMove(data);
                }
            }
        }
        else {
            return null;
        }
    }, [onSwipeStart, onSwipeMove, swipeBoundary]);
    const touchend = (0, react_1.useCallback)((ev) => {
        if (state.current) {
            const newState = handleTouchMove(ev, state.current);
            const wasSwiping = !!state.current.axis;
            if (wasSwiping && onSwipeEnd) {
                state.current = newState;
                onSwipeEnd(swipeData(state.current, getBoundary(ev, state.current.axis, swipeBoundary)));
            }
        }
    }, [onSwipeEnd, swipeBoundary]);
    return ((0, jsx_runtime_1.jsx)(NativeListener_1.NativeListener, Object.assign({ name: "Swipe" }, { touchstart, touchmove, touchend }, { children: children })));
}
exports.Swipe = Swipe;
function handleTouchStart(ev, { horizontal, vertical }) {
    const touch = ev.touches.item(0);
    if (!touch) {
        return undefined;
    }
    const startingPoint = {};
    if (horizontal) {
        startingPoint.x = touch.clientX;
    }
    if (vertical) {
        startingPoint.y = touch.clientX;
    }
    return {
        startingPoint,
        lastPosition: startingPoint,
        timestamp: Date.now(),
    };
}
function handleTouchMove(ev, state) {
    var _a, _b;
    const touch = ev.touches.item(0);
    if (!touch) {
        return state;
    }
    if (!state.axis) {
        const initialState = state;
        const timestamp = Date.now();
        let offsetX;
        let offsetY;
        if (typeof initialState.startingPoint.x === 'number') {
            const offset = touch.clientX - initialState.startingPoint.x;
            if (Math.abs(offset) > MINIMUM_SWIPE_OFFSET) {
                offsetX = offset;
            }
        }
        if (typeof initialState.startingPoint.y === 'number') {
            const offset = touch.clientY - initialState.startingPoint.y;
            if (Math.abs(offset) > MINIMUM_SWIPE_OFFSET) {
                offsetY = offset;
            }
        }
        if (typeof offsetX === 'number' && typeof offsetY === 'number') {
            if (Math.abs(offsetX) > Math.abs(offsetY)) {
                offsetY = undefined;
            }
            else {
                offsetX = undefined;
            }
        }
        if (offsetX) {
            const lastX = (_a = initialState.lastPosition.x) !== null && _a !== void 0 ? _a : 0;
            return {
                axis: 'x',
                startingPoint: optimizeStartingPoint(offsetX, initialState.startingPoint.x),
                lastPosition: touch.clientX,
                timestamp,
                inertia: inertia(touch.clientX - lastX, timestamp - initialState.timestamp),
            };
        }
        if (offsetY) {
            const lastY = (_b = initialState.lastPosition.y) !== null && _b !== void 0 ? _b : 0;
            return {
                axis: 'y',
                startingPoint: optimizeStartingPoint(offsetY, initialState.startingPoint.y),
                lastPosition: touch.clientY,
                timestamp,
                inertia: inertia(touch.clientY - lastY, timestamp - initialState.timestamp),
            };
        }
        return Object.assign(Object.assign({}, initialState), { lastPosition: { x: touch.clientX, y: touch.clientY }, timestamp });
    }
    const movingState = state;
    const timestamp = Date.now();
    const position = movingState.axis === 'x' ? touch.clientX : touch.clientY;
    return Object.assign(Object.assign({}, movingState), { lastPosition: movingState.axis === 'x' ? touch.clientX : touch.clientY, timestamp, inertia: inertia(position - movingState.lastPosition, timestamp - movingState.timestamp) });
}
function optimizeStartingPoint(offset, point = 0) {
    return offset > 0
        ? point + MINIMUM_SWIPE_OFFSET
        : point - MINIMUM_SWIPE_OFFSET;
}
function inertia(deltaDistance, deltaTime) {
    return deltaDistance / deltaTime;
}
function swipeData(state, swipeBoundary) {
    const { axis, lastPosition, startingPoint } = state;
    const offset = lastPosition - startingPoint;
    const execute = (offset > swipeBoundary && state.inertia >= 0) ||
        (offset < -swipeBoundary && state.inertia <= 0) ||
        (offset > 0 && state.inertia > MIN_INERTIA) ||
        (offset < 0 && state.inertia < -MIN_INERTIA);
    return { axis, offset, execute };
}
function getBoundary(ev, axis, override) {
    var _a, _b;
    const elSize = axis === 'x'
        ? (_a = ev.currentTarget) === null || _a === void 0 ? void 0 : _a.clientWidth
        : (_b = ev.currentTarget) === null || _b === void 0 ? void 0 : _b.clientHeight;
    const windowSize = axis === 'x' ? window.innerWidth : window.innerHeight;
    return override !== null && override !== void 0 ? override : (elSize !== null && elSize !== void 0 ? elSize : windowSize) / 2;
}
